import { AxiosError, AxiosResponse } from "axios";
import dataConstants from "../constants/dataConstants";
import axiosInstance from "./api";
import TestsListResponse from "./models/API/responseModels/testsListResponse.model";
import OnlineOrderValuesModel from "./models/formModels/onlineOrderValues.model";
import SelectTestCardProps from "./models/selectTestCardProps.model";
import TestListModel from "./models/testList.model";
import FileActions from "./utils/FileActions";
import { ResponseDocumentType } from "./models/supportingDocs.model";
import moment from "moment";
import toastMessages from "./utils/helpers/toastMessage";

type ObjectWithFiles = {
  [key: string]: File;
};

class OnlineOrderActions {
  textForPopup: string = "";
  private readonly classificationLabelsStorageKey =
    "supportingDocumentsClassificationLabelsMap";
  private readonly activePathsStorageKey = "supportingDocumentsActivePaths";

  private readonly labelToSupportingDocumentsKeyMap: Record<string, string> = {
    "current prescription medications": "currentPrescriptionMedications",
    "endoscopy report": "endoscopyReport",
    "path report": "pathologyReport",
    "pathology report": "pathologyReport",
    "patient insurance": "insuranceCard",
    "insurance card": "insuranceCard",
    "patient demo": "demographicsSheet",
    "patient demographics": "demographicsSheet",
    "demographics sheet": "demographicsSheet",
    "clinical notes": "clinicNotes",
    "clinic notes": "clinicNotes",
    "visit notes": "visitNotes",
    "visit note": "visitNotes",
    lomn: "lomn",
    "requisition form": "requisitionForm",
    other: "other",
  };

  constructor(
    private readonly selectedOrganization: number | null,
    private readonly selectedPhysician: number | null,
    private readonly selectedPhysicianData: any,
    private readonly roles: string[],
    private readonly userId: string,
    private readonly selectedPathLab?: number | null,
    private readonly orderData?: OnlineOrderValuesModel | undefined,
    private readonly token?: string
  ) {
    this.textForPopup = "";
  }

  private normalizePath(path: string): string {
    return decodeURIComponent(path).replace(/^\/+/, "").toLowerCase();
  }

  private getStoredClassificationLabelsMap(): Record<string, string[]> {
    try {
      const raw = sessionStorage.getItem(this.classificationLabelsStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  private getStoredActivePathsSet(): Set<string> {
    try {
      const raw = sessionStorage.getItem(this.activePathsStorageKey);
      if (!raw) return new Set<string>();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<string>();
      return new Set(parsed.map((path: string) => this.normalizePath(path)));
    } catch {
      return new Set<string>();
    }
  }

  private getLabelsForPathFromStorage(path: string): string[] {
    const storedLabelsMap = this.getStoredClassificationLabelsMap();
    const normalizedPath = this.normalizePath(path);
    if (storedLabelsMap[normalizedPath]) {
      return storedLabelsMap[normalizedPath] ?? [];
    }

    const matchedKey = Object.keys(storedLabelsMap).find((storedPath) => {
      const normalizedStoredPath = this.normalizePath(storedPath);
      return (
        normalizedStoredPath === normalizedPath ||
        normalizedStoredPath.endsWith(normalizedPath) ||
        normalizedPath.endsWith(normalizedStoredPath)
      );
    });

    return matchedKey ? storedLabelsMap[matchedKey] ?? [] : [];
  }

  private getAllSupportingDocumentPaths(
    supportingDocuments: Record<string, any>
  ): string[] {
    const allPaths = Object.values(supportingDocuments ?? {}).flatMap((value: any) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map((item: any) => this.blobPathFromSupportingValue(item))
          .filter(Boolean);
      }
      const single = this.blobPathFromSupportingValue(value);
      return single ? [single] : [];
    }) as Array<string | null>;

    return Array.from(new Set(allPaths.filter((path): path is string => !!path)));
  }

  private ensureSupportingDocumentsFromStoredLabels(
    supportingDocuments: Record<string, any>
  ): Record<string, any> {
    const mergedSupportingDocuments: Record<string, string[]> = {};

    Object.keys(supportingDocuments ?? {}).forEach((key) => {
      const value = supportingDocuments[key];
      if (Array.isArray(value)) {
        mergedSupportingDocuments[key] = value.filter(Boolean);
      } else if (typeof value === "string") {
        mergedSupportingDocuments[key] = [value];
      } else {
        mergedSupportingDocuments[key] = [];
      }
    });

    const allPaths = this.getAllSupportingDocumentPaths(supportingDocuments);
    allPaths.forEach((path) => {
      const labels = this.getLabelsForPathFromStorage(path);
      labels.forEach((label) => {
        const mappedKey =
          this.labelToSupportingDocumentsKeyMap[label?.trim()?.toLowerCase?.()];
        if (!mappedKey) return;
        if (!mergedSupportingDocuments[mappedKey]) {
          mergedSupportingDocuments[mappedKey] = [];
        }
        if (!mergedSupportingDocuments[mappedKey].includes(path)) {
          mergedSupportingDocuments[mappedKey].push(path);
        }
      });
    });

    return mergedSupportingDocuments;
  }

  private UploadFiles({
    files,
    token,
  }: {
    files: ObjectWithFiles | File[];
    token: string;
  }): Promise<Object | string[]> {
    const fileActions = new FileActions();
    const isArrayOfFiles = Array.isArray(files);
    let promises: any = [];
    // Check if files is an object with multiple files per key or a simple array of files
    if (!isArrayOfFiles) {
      Object.keys(files).forEach((key) => {
        if (Array.isArray(files[key])) {
          files[key].forEach((file: File | string) => {
            if (file instanceof File) {
              promises.push(
                fileActions.uploadToAzure({
                  type: "document",
                  file,
                  token,
                })
              );
            } else {
              promises.push(Promise.resolve(file));
            }
          });
        } else if (files[key] instanceof File) {
          // Handle single file (backward compatibility)
          promises.push(
            fileActions.uploadToAzure({
              type: "document",
              file: files[key],
              token,
            })
          );
        }
      });
    } else {
      promises = files.map((file) =>
        fileActions.uploadToAzure({ type: "document", file, token })
      );
    }

    return new Promise((resolve, reject) => {
      Promise.allSettled(promises).then((results) => {
        const updatedFilesPayload: any = isArrayOfFiles ? [] : {};

        if (!isArrayOfFiles) {
          // For object with multiple files per key
          const fileKeys = Object.keys(files);
          let resultIndex = 0;

          fileKeys.forEach((key) => {
            if (Array.isArray(files[key])) {
              // Handle array of files
              const fileArray = files[key] as File[];
              updatedFilesPayload[key] = [];

              for (let i = 0; i < fileArray.length; i++) {
                const result = results[resultIndex++];
                if (result?.status === "fulfilled" && result?.value) {
                  updatedFilesPayload[key].push(`${result.value}`);
                }
              }
            } else if (files[key] instanceof File) {
              // Handle single file (backward compatibility)
              const result = results[resultIndex++];
              if (result.status === "fulfilled" && result.value) {
                updatedFilesPayload[key] = `${result.value}`;
              }
            }
          });
        } else {
          // For simple array of files
          results.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
              updatedFilesPayload.push(`${result.value}`);
            }
          });
        }

        resolve(updatedFilesPayload);
      });
    });
  }

  /** Blob key `documents/...` only — never send presigned URLs on create/update. */
  private blobPathFromSupportingValue(value: any): string | null {
    if (!value) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) {
        try {
          const pathname = new URL(trimmed).pathname.replace(/^\/+/, "");
          return pathname || null;
        } catch {
          return null;
        }
      }
      return decodeURIComponent(trimmed).split("?")[0].replace(/^\/+/, "") || null;
    }
    if (value?.container && value?.filename) {
      return `${value.container}/${value.filename}`
        .replace(/^\/+/, "")
        .split("?")[0];
    }
    if (typeof value?.url === "string") {
      return this.blobPathFromSupportingValue(value.url);
    }
    return null;
  }

  private normalizeSupportingDocumentValue(value: any): string | null {
    return this.blobPathFromSupportingValue(value);
  }

  private normalizeSupportingDocumentsList(
    value: any
  ): Array<string | File> | string | File | null {
    if (Array.isArray(value)) {
      return value
        .map((doc: any) => {
          if (doc instanceof File) return doc;
          return this.normalizeSupportingDocumentValue(doc);
        })
        .filter(Boolean) as Array<string | File>;
    }

    if (value instanceof File) return value;
    return this.normalizeSupportingDocumentValue(value);
  }

  public getSOF({
    values,
    token,
  }: {
    values: OnlineOrderValuesModel;
    token: string;
  }): Promise<Object> {
    return new Promise((resolve, reject) => {
      const isDualRole =
        this?.roles?.length > 1 && this.roles.includes("Physician");

      const isDualRoleAndPhysicianHimself =
        isDualRole && values?.physicianId == this.userId;
      // if user is COA user
      if (this?.roles?.includes("Collaborative Order Agreement")) {
        resolve({});
        return;
      }

      // if user is physician only
      if (this?.roles?.length === 1 && this.roles.includes("Physician")) {
        resolve({});
        return;
      }

      //  if user is dual role and selected physician is himself
      if (isDualRoleAndPhysicianHimself) {
        resolve({ noSOF: false, isExpiredSOF: false, sofMismatch: false });
        return;
      }

      const payload = {
        physicianId: values.physicianId,
        primaryTest: values.testTypeId,
        secondaryTest: values.secondaryTestId,
      };

      axiosInstance
        .post("v1/sof/sof-verification", payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((results) => {
          const { status, data } = results;
          if (status === 200) resolve({ ...data.data });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private async orderPayload(
    data: OnlineOrderValuesModel,
    token: string
  ): Promise<Object> {
    const isExposedToRadiationValue =
      (Number(data.testTypeId) === 4 || Number(data.testTypeId === 7)) &&
        data.isExposedToRadiation === "N/A"
        ? null
        : data.isExposedToRadiation === null
          ? ""
          : Number(data.testTypeId) === 4 || Number(data.testTypeId === 7)
            ? data.isExposedToRadiation
            : "";

    let payload: any = {
      ...data,
      currentDate: moment().format("MM-DD-YYYY"),
      testTypeId: data?.testTypeId?.toString?.(),
      isSupportingDocuments: data.isSupportingDocuments === true ? 1 : 0,
      isAdditionalClinician:
        data.isAdditionalClinician === true
          ? 1
          : data.isAdditionalClinician === false
            ? 0
            : data.isAdditionalClinician === null
              ? 0
              : data.isAdditionalClinician,
      billingInfo: {
        ...data.billingInfo,
        primaryInsuranceId:
          data?.billingInfo?.primaryInsuranceId?.toString() === "19"
            ? ""
            : data?.billingInfo?.primaryInsuranceId?.toString?.() ?? "",
        icdCodeId: data?.billingInfo?.icdCodeId?.toString?.() ?? "",
        dateOfDischarge:
          data?.billingInfo?.typeOfFacility === "Non-Hospital" ||
            data?.billingInfo?.typeOfFacility === "Hospital Outpatient"
            ? ""
            : data?.billingInfo?.dateOfDischarge,
      },
      secondaryTestId: data?.secondaryTestId?.toString?.() ?? "",
      lymphNodeStatus:
        data?.isSentinelLymphNodeBiopsy === "Yes"
          ? data?.lymphNodeStatus?.toString?.()
          : "",
      laboratoryInfo: {
        ...data.laboratoryInfo,
        labortaryId: data?.laboratoryInfo?.labortaryId?.toString?.() ?? "",
      },
      patientInfo: {
        ...data.patientInfo,
        gender: data.patientInfo.gender?.toLowerCase(),
      },
      isExposedToRadiation: isExposedToRadiationValue,
      isReportPrame:
        data.isReportPrame === "Yes"
          ? true
          : data.isReportPrame === "No"
            ? false
            : false,
    };

    let existingTransitDocs = payload.transitDocuments.filter(
      (doc: ResponseDocumentType) => doc.filename
    );
    const haveFiles = () =>
      Object.keys(data.supportingDocuments).some((key) => {
        type ObjectKey = keyof typeof data.supportingDocuments;
        const myKey = key as ObjectKey;

        return Array.isArray(data.supportingDocuments[myKey])
          ? data.supportingDocuments[myKey].some((file: any) => file instanceof File)
          : data.supportingDocuments[myKey] instanceof File;
      });

    const hasTransitDocuments = () =>
      data.transitDocuments.some((doc: any) => doc instanceof File);

    if (payload.isSupportingDocuments) {
      const normalizedSupportingDocuments = Object.keys(
        payload?.supportingDocuments ?? {}
      ).reduce((acc: Record<string, Array<string | File> | string | File | null>, key) => {
        acc[key] = this.normalizeSupportingDocumentsList(
          payload?.supportingDocuments?.[key]
        );
        return acc;
      }, {});

      payload = {
        ...payload,
        supportingDocuments: normalizedSupportingDocuments,
      };

      const activePathsSet = this.getStoredActivePathsSet();
      if (activePathsSet.size > 0) {
        Object.keys(payload.supportingDocuments ?? {}).forEach((key) => {
          const value = payload.supportingDocuments[key];
          if (Array.isArray(value)) {
            payload.supportingDocuments[key] = value.filter((path: any) => {
              if (typeof path !== "string") return false;
              return activePathsSet.has(this.normalizePath(path));
            });
          } else if (typeof value === "string") {
            payload.supportingDocuments[key] = activePathsSet.has(
              this.normalizePath(value)
            )
              ? value
              : [];
          }
        });
      }

      payload.supportingDocuments = this.ensureSupportingDocumentsFromStoredLabels(
        payload.supportingDocuments
      );
    }

    if (existingTransitDocs.length > 0 && !hasTransitDocuments()) {
      payload = {
        ...payload,
        transitDocuments: existingTransitDocs.map(
          (doc: ResponseDocumentType) => `${doc.container}/${doc.filename}`
        ),
      };
    }

    delete payload.icdCodeDetails;

    if (payload?.testTypeId?.toString?.() === "8") {
      payload.billingInfo.typeOfFacility = null;
      delete payload.laboratoryInfo.medicarePullDate;
      delete payload.supportingDocuments.pathologyReport;
      delete payload.supportingDocuments.clinicNotes;
    } else {
      payload.medicarePullDate = payload.laboratoryInfo.medicarePullDate;
      delete payload.laboratoryInfo.medicarePullDate;
      delete payload.supportingDocuments.currentPrescriptionMedications;
      if (payload?.testTypeId?.toString?.() !== "9") {
        delete payload.supportingDocuments.clinicNotes;
      }
    }

    // These keys may exist for display purposes but are not accepted by the create/update API.
    if (payload?.supportingDocuments) {
      delete payload.supportingDocuments.orderSOF;
      delete payload.supportingDocuments.orderSummary;
    }

    if (!!payload?.patientInfo?.testTypeId) {
      delete payload.patientInfo.testTypeId;
    }

    if (!!payload?.lastUpdated) {
      delete payload.lastUpdated;
    }

    if (!!payload?.patientInfo?.organizationId) {
      delete payload?.patientInfo?.organizationId;
    }

    if (payload.isAdditionalClinician) {
      const newClinicians = payload.additionalClinician.filter(
        (clin: any) => !!clin.id === false
      );
      payload = {
        ...payload,
        additionalClinician: newClinicians,
        isAdditionalClinician: newClinicians.length > 0 ? 1 : 0,
      };
    }

    delete payload.acknowledgementOfClinician;
    delete payload.status;
    delete payload.sofExpiryDate;

    // if (payload?.testTypeId !== 3 || payload?.testTypeId !== 6) {
    //   delete payload.isReportPrame;
    // }

    if (payload?.id) delete payload.id;

    // Ensure patientId is always sent (required when order is for a duplicated patient in selected org)
    payload.patientId = data.patientId ?? payload.patientId ?? "";

    return new Promise(async (resolve, reject) => {
      let uploadedFiles = null;
      let uploadedTransitFiles: any = [];
      let sofData = null;

      if (haveFiles()) {
        uploadedFiles = await this.UploadFiles({
          files: payload.supportingDocuments,
          token,
        });

        payload = {
          ...payload,
          isSupportingDocuments: 1,
          supportingDocuments: {
            ...payload.supportingDocuments,
            ...uploadedFiles,
          },
        };
      }
      if (hasTransitDocuments()) {
        let newFiles = payload.transitDocuments.filter(
          (doc: any) => doc instanceof File
        );

        uploadedTransitFiles = await this.UploadFiles({
          files: newFiles,
          token,
        });
        if (existingTransitDocs.length > 0) {
          payload = {
            ...payload,
            transitDocuments: [
              ...existingTransitDocs.map(
                (doc: ResponseDocumentType) => `${doc.container}/${doc.filename}`
              ),
              ...uploadedTransitFiles,
            ],
          };
        } else
          payload = {
            ...payload,
            transitDocuments: uploadedTransitFiles,
          };
      }
      //  if (data.isPhysicianSignPermission) {
      //   try {
      //    sofData = await this.getSOF({
      //     values: payload,
      //     token,
      //    });
      //    payload = {
      //     ...payload,
      //     ...sofData,
      //    };
      //   } catch (error) {
      //    reject(error);
      //   }
      //  }

      resolve(payload);
    });
  }

  private isNotMissingValueFromContext(): boolean {
    // 1. Check for Unauthorized Users
    const isUnauthorized = this.roles.length === 0 || (this.roles.includes("PathLab User") && this.roles.length === 1);
    if (isUnauthorized) {
      this.textForPopup = "Not authorized to create order";
      return false;
    }

    // 2. Office Manager Logic
    if (this.roles.includes("Office Manager")) {
      if (!this.selectedOrganization) {
        this.textForPopup = "Please specify the organization and clinician in the context for whom you will be starting the test order for.";
        return false;
      }
      if (!this.selectedPhysician) {
        this.textForPopup = "Please select a clinician before proceeding with creating a test order.";
        return false;
      }
      return true;
    }

    // 3. Collaborative Order Agreement (COA) Logic
    if (this.roles.includes("Collaborative Order Agreement")) {
      if (!this.selectedOrganization) {
        this.textForPopup = "Please specify the organization and clinician in the context for whom you will be starting the test order for.";
        return false;
      }

      // This specifically addresses your requirement for null/missing Physician
      if (!this.selectedPhysician) {
        this.textForPopup = "Please select a clinician first before proceeding.";
        return false;
      }

      if (!this.selectedPathLab) {
        this.textForPopup = "Please select a Path Lab before proceeding with creating a test order.";
        return false;
      }

      return true;
    }

    // 4. Physician Logic
    if (this.roles.includes("Physician")) {
      if (!this.selectedOrganization) {
        this.textForPopup = "Please select an organization first";
        return false;
      }
      return true;
    }

    // Default fallback
    return !!this.selectedOrganization;
  }

  async transformTestsListResponse(res: string, roles: string[]) {
    const shouldGetAllowedTests = this.roles.includes("Collaborative Order Agreement") && !!this.selectedPhysician && !(this.selectedPhysicianData?.roles?.includes('Collaborative Order Agreement') && this.selectedPhysicianData?.roles?.includes('Physician'));
    let dermatologySequence: any = {
      2: 4,
      3: 1,
      5: 3,
      6: 2,
    };
    let response: TestsListResponse = JSON.parse(res);
    response = {
      ...response,
      data: response.data.map((test) => ({
        ...test,
        testTypes: test.testTypes
          .map((testType, index) => {
            if (test.id === 2) {
              return {
                ...testType,
                sequence: dermatologySequence[testType.id],
              };
            }
            return {
              ...testType,
              sequence: index + 1,
            };
          })
          .sort((a, b) => a.sequence - b.sequence),
      })),
    };

    if (response?.data?.length > 0) {
      const shouldGetAllowedTests = this.roles.includes("Collaborative Order Agreement") && !!this.selectedPhysician && !(this.selectedPhysicianData?.roles?.includes('Collaborative Order Agreement') && this.selectedPhysicianData?.roles?.includes('Physician'));
      if (shouldGetAllowedTests) {
        const allowedTests = await this.getAllowedTests();
        const filteredGroup = response?.data?.filter((test) => {
          const shouldReturn = test?.testTypes?.some((testType) =>
            allowedTests?.data?.data.includes?.(testType.code)
          );
          return shouldReturn;
        });

        return {
          ...response,
          data:
            filteredGroup?.length > 0
              ? filteredGroup.map((test) => ({
                ...test,
                testTypes: test.testTypes.filter((testType) =>
                  allowedTests.data.data.includes(testType.code)
                ),
              }))
              : [],
        };
      } else {
        return {
          ...response,
        };
      }
    } else return response;
  }

  getAllowedTests() {
    return axiosInstance.get(
      `/v1/order-agreement/get-allowed-tests?userId=${this.selectedPhysician}&pathLabId=${this.selectedPathLab}&organizationId=${this.selectedOrganization}`,
      { headers: { Authorization: `Bearer ${this.token}` } }
    );
  }

  async allowedToCreateOrder(): Promise<boolean> {
    const shouldGetAllowedTests = this.roles.includes("Collaborative Order Agreement") && !!this.selectedPhysician && !(this.selectedPhysicianData?.roles?.includes('Collaborative Order Agreement') && this.selectedPhysicianData?.roles?.includes('Physician'));

    return new Promise((resolve, reject) => {
      if (!this.isNotMissingValueFromContext()) return resolve(false);

      if (shouldGetAllowedTests) {
        this.getAllowedTests()
          .then(({ data: { data } }) => {
            if (data?.length === 0) {
              resolve(false);
              this.textForPopup = "Not authorized to place any order";
            } else {
              resolve(true);
            }
          })
          .catch((error: AxiosError<any>) => {
            this.textForPopup = error.response?.data?.message ?? error.message;
            resolve(false);
          });
      }
      return resolve(true);
    });
  }

  async allowedToUpdateOrder(): Promise<boolean> {
    const physiciansDontMatch =
      Number(this.orderData?.physicianId) !== this.selectedPhysician;
    const submittedOrder = this.orderData?.status && this.orderData.status >= 40;
    const shouldGetAllowedTests = this.roles.includes("Collaborative Order Agreement") && !!this.selectedPhysician && !(this.selectedPhysicianData?.roles?.includes('Collaborative Order Agreement') && this.selectedPhysicianData?.roles?.includes('Physician'));

    return new Promise((resolve, reject) => {
      if (!this.isNotMissingValueFromContext()) return resolve(false);
      if (physiciansDontMatch) {
        this.textForPopup = "Selected Physician and Order physician do not match";
        return resolve(false);
      }

      if (submittedOrder) {
        this.textForPopup = "Order cannot be edited once submitted";
        return resolve(false);
      }

      if (shouldGetAllowedTests) {
        this.getAllowedTests()
          .then(({ data: { data } }) => {
            if (data?.length === 0) {
              resolve(false);
              this.textForPopup = "Not authorized to place any order";
            } else {
              resolve(true);
            }
          })
          .catch((error: AxiosError<any>) => {
            this.textForPopup = error.response?.data?.message ?? error.message;
            resolve(false);
          });
      }
    });
  }

  popUpText(): string {
    return this.textForPopup;
  }

  getTestList(data: TestsListResponse, activeIndex: number): TestListModel[] {
    return data?.data?.map(
      (test): TestListModel => ({
        activeIndex,
        title: test.name,
        tests: test?.testTypes?.map(
          (testType): SelectTestCardProps => ({
            ...test,
            id: testType.id,
            groupId: testType.groupId,
            isFavorite: false,
            handleFav: () => { },
            handleSelection: () => { },
            selected: false,
            DecisionDxPRAMEDescription: testType?.DecisionDxPRAMEDescription,
            DecisionDxPRAMELogo: testType.DecisionDxPRAMELogo,
            testDescription: testType?.name?.split?.(":")?.[1] ?? testType.name,
            testName: testType?.name?.split?.(":")?.[0] ?? testType.name,
          })
        ),
      })
    );
  }

  resetValues(
    testTypeId: number,
    setFieldValue: Function,
    setFieldTouched: Function,
    previousTestTypeId: number
  ): void {
    if (previousTestTypeId !== testTypeId) {
      Object.keys(dataConstants.onlineOrderResetValues).map((key) => {
        type ValueKey = keyof typeof dataConstants.onlineOrderResetValues;
        const myKey = key as ValueKey;

        setFieldValue(key, dataConstants.onlineOrderResetValues[myKey], true);
        setTimeout(() => setFieldTouched(key, true));
      });
    }
  }

  async createOrder({
    data,
    token,
  }: {
    data: OnlineOrderValuesModel;
    token: string;
  }) {
    try {
      const payload = await this.orderPayload(data, token);
      return axiosInstance.post(
        `/v1/orders/new-flow/create`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          transformResponse: (res: string) => {
            const response = JSON.parse(res);
            return {
              ...response,
              data: { ...response.data, ...data },
            };
          },
        }
      );
    } catch (error: any) {
      throw new Error(error.message);
    }
  }
  async updateOrder({
    data,
    token,
    id,
  }: {
    data: OnlineOrderValuesModel;
    token: string;
    id: string;
  }) {
    try {
      const payload: any = await this.orderPayload(data, token);
      delete payload.isPotentialDuplicateOrder;
      return axiosInstance.put(
        `/v1/orders/new-flow/${id}`,
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          transformResponse: (res: string) => {
            const response = JSON.parse(res);
            return {
              ...response,
              data: { ...response.data, ...data },
            };
          },
        }
      );
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  async deleteOrder({ id, token }: { id: string; token: string }) {
    try {
      return axiosInstance.delete(`/v1/orders/new-flow/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error: any) {
      throw new Error(error);
    }
  }

  transformResponse(res: string) {
    let response: AxiosResponse<OnlineOrderValuesModel | any> = JSON.parse(res);

    if (response?.data) {
      const responseData: any = response.data;
      const isAdditionalClinician =
        response?.data?.additionalClinician?.length > 0
          ? true
          : responseData.isAdditionalClinician === null
            ? false
            : responseData.isAdditionalClinician;
      const isAttestation =
        response?.data.additionalClinician?.length > 0
          ? true
          : response.data.isAttestation;

      const endoscopyReportDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "endoscopy-report"
        );
      const pathologyReportDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "patholog-report"
        );
      const currentPrescriptionMedicationsDocuments: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "current-prescription-medications"
        );
      const insuranceCard: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "insurance-card"
        );
      const demographicsSheet: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "demographics-sheet"
        );
      const clinicNotesDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "clinic-notes"
        );
      const requisitionFormDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter(
          (doc: any) => doc.type === "requisition-form"
        );
      const visitNotesDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter((doc: any) => doc.type === "visit-notes");
      const lomnDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter((doc: any) => doc.type === "lomn");
      const otherDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter((doc: any) => doc.type === "other");
      const orderSofDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter((doc: any) => doc.type === "order-sof");
      const orderSummaryDocument: ResponseDocumentType =
        response?.data?.supportingDocuments?.filter((doc: any) => doc.type === "order-summary");

      const primaryInsuranceId =
        response?.data?.billingInfo.primaryInsuranceId === null &&
          !!response?.data?.billingInfo?.insuranceCompanyName
          ? 19
          : response.data.billingInfo.primaryInsuranceId;

      const supportingDocuments: any = {
        currentPrescriptionMedications: !!currentPrescriptionMedicationsDocuments
          ? currentPrescriptionMedicationsDocuments
          : null,
        endoscopyReport: !!endoscopyReportDocument ? endoscopyReportDocument : null,
        pathologyReport: !!pathologyReportDocument ? pathologyReportDocument : null,
        insuranceCard: !!insuranceCard ? insuranceCard : null,
        demographicsSheet: !!demographicsSheet ? demographicsSheet : null,
        clinicNotes: !!clinicNotesDocument ? clinicNotesDocument : null,
        requisitionForm:
          Array.isArray(requisitionFormDocument) && requisitionFormDocument.length > 0
            ? requisitionFormDocument
            : null,
        visitNotes:
          Array.isArray(visitNotesDocument) && visitNotesDocument.length > 0
            ? visitNotesDocument
            : null,
        lomn:
          Array.isArray(lomnDocument) && lomnDocument.length > 0 ? lomnDocument : null,
        other:
          Array.isArray(otherDocument) && otherDocument.length > 0 ? otherDocument : null,
        orderSOF:
          Array.isArray(orderSofDocument) && orderSofDocument.length > 0
            ? orderSofDocument
            : null,
        orderSummary:
          Array.isArray(orderSummaryDocument) && orderSummaryDocument.length > 0
            ? orderSummaryDocument
            : null,
      };

      const orderDetail: OnlineOrderValuesModel = {
        id: !!response?.data?.id ? response.data.id : "",
        isSave: "false",
        isConsideredForTherapy: response?.data?.isConsideredForTherapy ?? "",
        facilityType: response?.data?.facilityType ?? "",
        isSentinelLymphNodeBiopsy: response.data.isSentinelLymphNodeBiopsy,
        isPrimaryCutaneousMelanocyticNeoplasm:
          response?.data?.isPrimaryCutaneousMelanocyticNeoplasm ?? "",
        isClinicallyPalpableNodes: response.data.isClinicallyPalpableNodes,
        isIntrasitMetastasis: response.data.isIntrasitMetastasis,
        icd10CodeMismatch: response.data.icd10CodeMismatch,
        medicationMismatch: response.data.medicationMismatch,
        pathologySurgicalFindings: response.data.pathologySurgicalFindings,
        historyPhysicalExam: response.data.historyPhysicalExam,
        otherIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement:
          response.data.otherIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement,
        isPhysicianSignPermission: response.data.isPhysicianSignPermission,
        currentDate: response.data.currentDate,
        additionalCodes: response.data.additionalCodes,
        testTypeId: Number(response.data.testTypeId),
        secondaryTestId: !!response.data.secondaryTestTypeId
          ? Number(response.data.secondaryTestTypeId)
          : response.data.secondaryTestTypeId,
        organizationId: response.data.organizationId,
        physicianId: response.data.physicianId,
        billingInfo: {
          dateOfDischarge: !!response?.data?.billingInfo?.dateOfDischarge
            ? response.data.billingInfo.dateOfDischarge
            : "",
          primaryInsuranceId,
          icdCodeId: response.data.billingInfo.icdCodeId,
          insuranceCompanyName: response.data.billingInfo.insuranceCompanyName,
          phone: response.data.billingInfo.phone,
          policyNumber: response.data.billingInfo.policyNumber,
          typeOfFacility: response?.data?.billingInfo?.typeOfFacility ?? "",
        },
        notes: response.data.notes,
        laboratoryInfo: {
          labortaryId: response.data.labortaryInfo.labortaryId,
          labortaryName: response.data.labortaryInfo.labortaryName,
          dateOfCollection: response.data.labortaryInfo.dateOfCollection,
          FacilityContactPerson: response.data.labortaryInfo.FacilityContactPerson,
          labortaryPhone: response.data.labortaryInfo.phone,
          labortaryFax: response.data.labortaryInfo.fax,
          specimenId: response.data.labortaryInfo.specimenId,
          typeOfSpecimen: response.data.labortaryInfo.typeOfSpecimen,
          sampleLocationA: response?.data?.sampleLocationA ?? "",
          sampleLocationB: response?.data?.sampleLocationB ?? "",
          medicarePullDate: response.data.medicarePullDate,
        },
        patientId: response.data.patientId,
        patientName: response.data.patientName,
        patientInfo: {
          firstName: response.data.patientInfo.firstName,
          lastName: response.data.patientInfo.lastName,
          gender: response.data.patientInfo.gender,
          address: response.data.patientInfo.address,
          phone: response.data.patientInfo.phone,
          middleInitial: response.data.patientInfo.middleInitial,
          birthDate: response?.data?.patientInfo?.birthDate
            ? moment(response?.data?.patientInfo?.birthDate).format("MM-DD-YYYY")
            : response?.data?.patientInfo?.birthDate,
          email: response.data.patientInfo.email,
          securityInfo: {
            ssn: responseData.patientInfo.ssn,
            mrn: responseData.patientInfo.mrn,
          },
          city: response.data.patientInfo.city,
          state: response.data.patientInfo.state,
          zipcode: response.data.patientInfo.zipcode,
        },
        additionalClinician: response.data.additionalClinician,
        isAdditionalClinician,
        supportingDocuments,
        isAttestation,
        isTeleHealth: response.data.isTeleHealth,
        isExpiredSOF: response.data.isExpiredSOF,
        sofMismatch: response.data.sofMismatch,
        noSOF: response.data.noSOF,
        isSupportingDocuments: response.data.isSupportingDocuments,
        transitDocuments: !!response.data.transitDocument
          ? response.data.transitDocument
          : [],
        isExposedToRadiation:
          !!response?.data?.isExposedToRadiation === false
            ? "N/A"
            : response.data.isExposedToRadiation,
        isReportPrame:
          !!response?.data?.isReportPrame === true
            ? "Yes"
            : response.data.isReportPrame === false
              ? "No"
              : response.data.isReportPrame,
        tubeBarcode: response.data.tubeBarcode,
        tumorSite: response.data.tumorSite,
        isAltheaDxNeuropsychotropicMedications:
          response?.data?.isAltheaDxNeuropsychotropicMedications ?? [],
        isIDgNeuropsychotropicMedicationsCurrent:
          response?.data?.isIDgNeuropsychotropicMedicationsCurrent ?? [],
        isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement:
          response?.data?.isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement ??
          [],
        status: response.data.status,
        lymphNodeStatus: response.data.lymphNodeStatus ?? "",
        isTissueShipped: response.data.isTissueShipped ?? false,
        shipTracking: response.data.shipTracking ?? "",
        shipMethod: response.data.shipMethod ?? "",
        guarantorName: response?.data?.guarantorName ?? "",
        icdCodeDetails: response?.data?.icdCodeDetails ?? undefined,
      };

      response = {
        ...response,
        data: {
          ...orderDetail,
        },
      };
    }

    return response;
  }
}

export default OnlineOrderActions;
